import { DOCUMENT } from '@angular/common';
import {
  ApplicationConfig,
  DestroyRef,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  PreloadAllModules,
  Router,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  provideTransloco,
  TRANSLOCO_MISSING_HANDLER,
  TRANSLOCO_TRANSPILER,
  Translation,
  TranslocoLoader,
  TranslocoService,
} from '@jsverse/transloco';

import {
  BeginPassphraseOnboarding,
  BuildTestamentDocument,
  CompleteOwnerProfile,
  ComputeCollectionTotal,
  CreateLedgerItem,
  CreateMessageItem,
  CreateDraft,
  ExportTestamentPdf,
  PreviewTestament,
  GenerateEmergencyCard,
  GenerateRecoveryCard,
  LoadDraft,
  ResolveApplicableSections,
  ResolveSectionQuestions,
  SaveSectionAnswers,
  TodayInHijri,
  UpdateOwnerProfile,
  UnlockPrivateVault,
  VerifyPassphrase,
  SuggestPassphrase,
  type UseCase,
} from '../application';
import {
  IsoDateTime,
  TestamentId,
  CountryCode,
  requiredValidationsArePresent,
  type OwnerProfile,
} from '../domain';
import {
  CIVIL_MODULE_HEADERS,
  CONTENT_CORE_META,
  INTEGRATED_COUNTRIES,
  loadContentLazily,
  loadContentPack,
} from './content-pack';
import {
  BrowserCalendarAdapter,
  BrowserClockAdapter,
  BrowserPrintPdfAdapter,
  DEFAULT_ARGON2ID_CONFIGURATION,
  LazyContentPort,
  IndexedDbDraftStore,
  PersistentDraftStore,
  BackupLedger,
  LegalLedger,
  browserDownload,
  handOverBackup,
  Argon2idKdf,
  WebCryptoAdapter,
  CryptoRandomIndex,
  LazyWordLists,
  resolveContentPackEnvironment,
  UmmAlQuraHijriAdapter,
} from '../infrastructure';
import {
  PublicJourneyConfig,
  PublicJourneyUseCases,
  type ReceivedBackupFile,
} from '../features/public-journey/public-journey.dependencies';
import { routes } from './app.routes';
import { FRENCH_CORE } from '../shared/i18n/catalogues/fr';
import { loadFrenchComplete, loadFrenchDeferred } from '../shared/i18n/french-catalogue';
import {
  LANGUAGES,
  SOURCE_LANGUAGE,
  directionOf,
  findLanguage,
  isSupportedLanguage,
  languageCodes,
} from '../shared/i18n/language-registry';
import { preferredLanguage } from '../shared/i18n/preferred-language';
import { preloadPrintSanitizer } from '../infrastructure/pdf/print-sanitizer';
import type { DraftStore } from '../application/ports';
import type { NativePhone } from '../infrastructure/native/native-phone';
import type { NativeStorage } from '../infrastructure/native/native-storage';
import {
  RUNS_NATIVE,
  openNativePhone,
  openBiometric,
  openNativeStorage,
} from '../infrastructure/native/platform/platform';
import { JOURNEY_PATHS } from '../features/public-journey/journey.paths';
import { revivePersistedDraft } from '../infrastructure/persistence/persisted-draft-revival';
import { preloadPrintTemplates } from '../infrastructure/pdf/print-templates';
import { ThemeStore } from '../features/public-journey/theme.store';
import {
  PassphraseCreationConfig,
  PassphraseCreationUseCases,
} from '../features/security/passphrase-creation.dependencies';
import { FrenchFallbackMissingHandler } from './i18n.missing-handler';
import { LocalisedNumbersTranspiler } from './i18n.transpiler';
import { PwaUpdateService } from './pwa-update.service';

/** Kept in sync by hand with the boot script in index.html, which runs before Angular exists. */
const LANGUAGE_STORAGE_KEY = 'mon-testament-language';

const storedLanguage = (): string | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : null;
  } catch {
    // Private windows and blocked site data throw on access, not only on write.
    return null;
  }
};

/**
 * What the reader's own browser says they read, matched against what the app has. Consulted only
 * when nothing is stored, so an explicit choice always wins over a preference.
 */
const browserLanguage = (): string | null => {
  if (typeof navigator === 'undefined') return null;
  const requested = navigator.languages ?? [navigator.language];
  return preferredLanguage(requested, languageCodes);
};

/** Local storage, where reaching for it does not itself throw. */
const safeLocalStorage = (): Storage | undefined => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
};

const rememberLanguage = (language: string): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage refused: the choice lasts the session, which beats failing the switch.
  }
};

const contentPackEnvironment = resolveContentPackEnvironment();

/*
 * In the apps the will lives in the app's own files, not in a browser's storage
 * (src/infrastructure/native). They are opened once, and main.ts starts Angular only after: the
 * registers read synchronously afterwards are read by then. Every use below sits behind
 * RUNS_NATIVE, which the web build folds away — no byte of it reaches the browser. (Not a
 * top-level await: a module that awaits makes the code splitter cut the first screen apart.)
 */
let NATIVE: NativeStorage | null = null;
/** The phone itself: its share sheet, its PDF files, the backups other apps hand over. */
let PHONE: NativePhone | null = null;
export const nativeReady = RUNS_NATIVE
  ? Promise.all([openNativeStorage({ draft: revivePersistedDraft }), openNativePhone()]).then(
      ([storage, phone]) => {
        NATIVE = storage;
        PHONE = phone;
      },
    )
  : (null as unknown as Promise<void>);

/**
 * The screens where the phrase is typed or shown — creation and its recovery card, the phrase in
 * Settings, its change, unlocking, restoring — by their address.
 */
const PHRASE_PATHS: readonly string[] = (
  ['passphrase-creation', 'security', 'security-change', 'unlock', 'restore'] as const
).map((view) => `/${JOURNEY_PATHS[view]}`);
const isPhraseScreen = (url: string): boolean => PHRASE_PATHS.includes(url.split(/[?#]/u)[0]!);

/** A backup another app opened this one with, until the restore screen takes it. */
const RECEIVED_BACKUP = RUNS_NATIVE ? signal<ReceivedBackupFile | null>(null) : null;
export { RUNS_NATIVE };

/** The native drafts, reached once the storage is open (main.ts waits for it). */
const nativeDraftStore: DraftStore = {
  load: (id) => NATIVE!.drafts.load(id),
  save: (draft) => NATIVE!.drafts.save(draft),
};

// Asks the browser to keep the will at the first write: without it, data a site seldom opened
// may be cleared, the draft with it.
const draftStore = RUNS_NATIVE
  ? nativeDraftStore
  : new PersistentDraftStore(
      new IndexedDbDraftStore('mon-testament-phase-5-public'),
      globalThis.navigator?.storage,
    );

/**
 * Whether the scholar has validated the religious content, as the content check answered when this
 * bundle was built (scripts/verify-content-validation.mjs, through ng-with-defines). While it is
 * false every page shows the validation banner and the printed documents carry the same mention;
 * once it is true the minifier drops both.
 */
const CONTENT_VALIDATED = (process.env['CONTENT_VALIDATED'] as unknown) === true;
const clock = new BrowserClockAdapter();
// In the apps the documents become PDF files made by the WebView, handed to the share sheet.
const pdf = new BrowserPrintPdfAdapter(
  RUNS_NATIVE ? (html) => PHONE!.printDocument(html) : undefined,
  undefined,
  !CONTENT_VALIDATED,
);

/**
 * Where i-dina-api answers — the contact form and the online backup alike — compiled in at build
 * time like the review access code. Absent by default, and absent is a decision: the screens then
 * offer nothing that would go nowhere. Cloudflare Pages passes it to the build that needs it.
 */
const IDINA_API_URL = process.env['IDINA_API_URL'];
const apiConfigured = typeof IDINA_API_URL === 'string' && IDINA_API_URL.length > 0;

const crypto = new WebCryptoAdapter(new Argon2idKdf());

/**
 * The adapter of the owner's country: its module's when its rules are built in, else the generic
 * one, which comes with the content rather than with the first screen.
 */
/** The « Dans votre pays » page of an owner living in `country`, read with the content. */
const civilPageFor = (country: CountryCode, regime: OwnerProfile['matrimonialRegime']) =>
  loadContentLazily().then((lazy) => lazy.civilPageFor(country.value, regime));

const juridicalAdapterFor = async (country: CountryCode) =>
  (await loadContentPack()).modules[country.value]?.adapter ??
  (await loadContentLazily()).genericAdapter(country);

/**
 * Whether the legal mentions the document prints for the owner's country may be printed as such:
 * its civil module's, validated by I-DINA on the official texts; or, where its rules are not built
 * in, the generic ones, which claim nothing of its law. The fiqh is the imam's, and the
 * production gate keeps it out of any build until he has validated it.
 */
const contentValidatedFor = (country: CountryCode): boolean => {
  const module = CIVIL_MODULE_HEADERS[country.value];
  return (
    module === undefined ||
    requiredValidationsArePresent(module.requiredValidations, module.meta.validatedBy)
  );
};

const initialOwner: OwnerProfile = Object.freeze({
  // Replaced by the phone's own guess when the questions begin: see proposeResidence.
  residenceCountry: CountryCode.create('BE'),
  burialPreference: 'residence',
  madhhab: 'unspecified',
  gender: 'female',
  maritalStatus: 'married',
  matrimonialRegime: 'separation',
  hasMinorChildren: false,
  isConvert: false,
});

/**
 * The country the phone suggests, guessed when the questions begin: the time-zone table and the
 * list of every country are fetched then, not with the first screen. Belgium if anything fails.
 */
const proposeResidence = async (): Promise<CountryCode> => {
  try {
    const { browserResidenceClues, guessResidence } =
      await import('../shared/countries/guess-residence');
    return CountryCode.create(guessResidence(browserResidenceClues()));
  } catch {
    return initialOwner.residenceCountry;
  }
};

/** The backup use cases, fetched the first time a backup is made or read. */
const loadBackup = () => import('../application/use-cases/backup.use-cases');
/**
 * The online backup — its use cases, the HTTP gateway and the account's database — fetched the
 * first time the account is looked at, and built once.
 */
let online: Promise<{
  readonly m: typeof import('../application/use-cases/online-backup.use-cases');
  readonly gateway: import('../application').RemoteBackupGateway;
  readonly accounts: import('../application').OnlineAccountStore;
}> | null = null;
const loadOnline = () =>
  (online ??= Promise.all([
    import('../application/use-cases/online-backup.use-cases'),
    import('../infrastructure/online/http-remote-backup-gateway'),
    import('../infrastructure/online/indexed-db-online-account-store'),
  ]).then(([m, http, store]) => ({
    m,
    gateway: apiConfigured
      ? new http.HttpRemoteBackupGateway(IDINA_API_URL)
      : new http.UnconfiguredRemoteBackupGateway(),
    accounts: RUNS_NATIVE ? NATIVE!.accounts : new store.IndexedDbOnlineAccountStore(),
  })));

/** The wasi's access: its use cases and gateway, fetched the first time and built once. */
/**
 * The phrase behind the fingerprint or the face, in the apps only: the plugin, the dates kept
 * around it and the rules, fetched the first time the unlock screen asks. The web build folds
 * RUNS_NATIVE away, and this with it.
 */
let biometric: Promise<import('../application').BiometricUnlock> | null = null;
const loadBiometric = () =>
  (biometric ??= Promise.all([
    import('../application/use-cases/biometric-unlock.use-cases'),
    openBiometric(),
  ]).then(([m, { vault, ledger }]) => new m.BiometricUnlock(vault, ledger, clock)));

let wasi: ReturnType<PublicJourneyUseCases['wasi']> | null = null;
const loadWasi = () =>
  (wasi ??= Promise.all([import('./wasi-access.wiring'), loadOnline()]).then(([m, { accounts }]) =>
    m.wasiAccessUseCases(IDINA_API_URL, crypto, accounts),
  ));

/**
 * The use cases of the screens after the first one — a collection's items, the wasi, the phrase,
 * the contact form — fetched together the first time one of them runs, and built once.
 */
let later: Promise<import('./later-use-cases.wiring').LaterUseCases> | null = null;
const loadLater = () =>
  (later ??= import('./later-use-cases.wiring').then((m) =>
    m.laterUseCases(draftStore, crypto, clock, IDINA_API_URL),
  ));

/** A use case built the first time it runs, and kept. */
const lazily = <Input, Output>(
  build: () => Promise<UseCase<Input, Output>>,
): UseCase<Input, Output> => {
  let built: Promise<UseCase<Input, Output>> | null = null;
  return {
    execute: async (input: Input): Promise<Output> => {
      built ??= build();
      return (await built).execute(input);
    },
  };
};

function publicJourneyUseCases(): PublicJourneyUseCases {
  // The sections and their rules are the heaviest thing the app holds, and the first screen needs
  // none of them: they are fetched the first time a part of the wishes is resolved.
  const content = new LazyContentPort(() =>
    Promise.all([loadContentPack(), loadContentLazily()]).then(
      ([pack, lazy]) => new lazy.DevelopmentContentPort(pack, contentPackEnvironment),
    ),
  );
  return {
    addCollectionItem: lazily(() => loadLater().then((later) => later.addCollectionItem)),
    beginPassphraseOnboarding: new BeginPassphraseOnboarding(crypto),
    changePassphrase: lazily(() => loadLater().then((later) => later.changePassphrase)),
    buildDocument: new BuildTestamentDocument(new BrowserCalendarAdapter()),
    completeProfile: new CompleteOwnerProfile(draftStore, clock),
    confirmPassphraseOnboarding: lazily(() =>
      loadLater().then((later) => later.confirmPassphraseOnboarding),
    ),
    computeCollectionTotal: new ComputeCollectionTotal(),
    createLedgerItem: new CreateLedgerItem(),
    createMessageItem: new CreateMessageItem(),
    createDraft: new CreateDraft(draftStore, clock),
    designateExecutor: lazily(() => loadLater().then((later) => later.designateExecutor)),
    removeExecutor: lazily(() => loadLater().then((later) => later.removeExecutor)),
    editCollectionItem: lazily(() => loadLater().then((later) => later.editCollectionItem)),
    exportPdf: new ExportTestamentPdf(pdf),
    previewTestament: new PreviewTestament(pdf),
    generateEmergencyCard: new GenerateEmergencyCard(pdf),
    generateRecoveryCard: new GenerateRecoveryCard(pdf),
    loadDraft: new LoadDraft(draftStore),
    recordExecutorNotice: lazily(() => loadLater().then((later) => later.recordExecutorNotice)),
    resolveSections: new ResolveApplicableSections(content),
    resolveQuestions: new ResolveSectionQuestions(),
    removeCollectionItem: lazily(() => loadLater().then((later) => later.removeCollectionItem)),
    // The guards of the bequests (W6): a chunk of their own, read on the bequests and the documents.
    validateFiqhRules: lazily(() =>
      import('../application/use-cases/validate-fiqh-rules.use-case').then(
        (m) => new m.ValidateFiqhRules(),
      ),
    ),
    resetPrivateVault: lazily(() => loadLater().then((later) => later.resetPrivateVault)),
    todayInHijri: new TodayInHijri(clock, new UmmAlQuraHijriAdapter(clock)),
    saveSectionAnswers: new SaveSectionAnswers(draftStore, crypto, clock),
    sendContactMessage: {
      configured: apiConfigured,
      ...lazily(() => loadLater().then((later) => later.sendContactMessage)),
    },
    updateProfile: new UpdateOwnerProfile(draftStore, clock),
    unlockPrivateVault: new UnlockPrivateVault(draftStore, crypto),
    verifyPassphrase: new VerifyPassphrase(draftStore, crypto),
    createBackup: lazily(() =>
      loadBackup().then((m) => new m.CreateBackup(draftStore, crypto, crypto, clock)),
    ),
    readBackup: lazily(() => loadBackup().then((m) => new m.ReadBackup(crypto, crypto))),
    restoreBackup: lazily(() => loadBackup().then((m) => new m.RestoreBackup(draftStore, crypto))),
    online: {
      loadAccount: lazily(() =>
        loadOnline().then(({ m, accounts }) => new m.LoadOnlineAccount(accounts)),
      ),
      requestCode: lazily(() =>
        loadOnline().then(({ m, gateway }) => new m.RequestSignInCode(gateway)),
      ),
      verifyCode: lazily(() =>
        loadOnline().then(({ m, gateway, accounts }) => new m.VerifySignInCode(gateway, accounts)),
      ),
      send: lazily(() =>
        loadOnline().then(
          ({ m, gateway, accounts }) =>
            new m.SendOnlineBackup(draftStore, crypto, crypto, gateway, accounts, clock),
        ),
      ),
      fetch: lazily(() =>
        loadOnline().then(({ m, gateway, accounts }) => new m.FetchOnlineBackup(gateway, accounts)),
      ),
      listRevisions: lazily(() =>
        loadOnline().then(
          ({ m, gateway, accounts }) => new m.ListOnlineRevisions(gateway, accounts),
        ),
      ),
      adopt: lazily(() =>
        loadOnline().then(({ m, accounts }) => new m.AdoptOnlineBackup(accounts)),
      ),
      signOut: lazily(() =>
        loadOnline().then(({ m, gateway, accounts }) => new m.SignOutOnline(gateway, accounts)),
      ),
      deleteAccount: lazily(() =>
        loadOnline().then(
          ({ m, gateway, accounts }) => new m.DeleteOnlineAccount(gateway, accounts),
        ),
      ),
    },
    wasi: loadWasi,
    biometric: RUNS_NATIVE ? loadBiometric : null,
  };
}

function publicJourneyConfig(): PublicJourneyConfig {
  return {
    draftId: TestamentId.create('phase-5-public-draft'),
    initialOwner,
    proposeResidence,
    nextReviewAt: IsoDateTime.create('2027-02-01T00:00:00Z'),
    juridicalAdapterFor,
    civilPageFor,
    integratedCountries: INTEGRATED_COUNTRIES,
    keyDerivation: DEFAULT_ARGON2ID_CONFIGURATION,
    // Fifteen minutes without a touch, a key or a scroll: reading a long part is not leaving it.
    inactivityMilliseconds: 15 * 60 * 1000,
    contentValidatedFor,
    validationBanner: !CONTENT_VALIDATED,
    nativeApp: RUNS_NATIVE && {
      receivedBackup: RECEIVED_BACKUP!,
      privateSpace: (open) => PHONE!.privateSpace(open),
    },
    preparePrinting: whenIdle,
    shareMessage: RUNS_NATIVE ? (message) => PHONE!.shareMessage(message) : shareSheet(),
    copyMessage: toClipboard,
    handOverBackup: RUNS_NATIVE
      ? (file) => PHONE!.handOverBackup(file)
      : (file) =>
          handOverBackup(file, {
            canShare: navigator.canShare?.bind(navigator),
            share: navigator.share?.bind(navigator),
            download: browserDownload(document),
          }),
    backupLedger: new BackupLedger(RUNS_NATIVE ? NATIVE!.backupLedger : safeLocalStorage()),
    contentPack: CONTENT_CORE_META.countryVersion,
    onlineBackupOffered: apiConfigured,
    deviceLabel: deviceLabel(),
    legalLedger: new LegalLedger(RUNS_NATIVE ? NATIVE!.legalLedger : safeLocalStorage()),
  };
}

/**
 * Each language but French arrives as its own chunk, fetched the first time it is asked for.
 * An unknown code falls back to the source catalogue rather than failing the render.
 */
class RegistryTranslocoLoader implements TranslocoLoader {
  async getTranslation(language: string): Promise<Translation> {
    const entry = findLanguage(language);
    if (entry === null) return (await loadFrenchComplete()) as Translation;
    return (await entry.load()) as Translation;
  }
}

/**
 * The print sanitizer, fetched while the reader is reading rather than while they are waiting.
 * It has to be in hand before the tap: iOS only allows `print()` while the gesture is still
 * live, and a network round trip in between loses it. `requestIdleCallback` is absent on
 * Safari, which is precisely the engine that cares, hence the timeout behind it.
 */
/**
 * The phone's own sharing sheet, or nothing where there is none. The message goes to whichever
 * app the owner picks — no server of ours is involved — and a cancelled sheet is not a failure:
 * it answers false, and the screen goes on as if nothing had been sent, because nothing was.
 */
const shareSheet = (): ((message: string) => Promise<boolean>) | null => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return null;
  return async (message: string): Promise<boolean> => {
    try {
      await navigator.share({ text: message });
      return true;
    } catch {
      return false;
    }
  };
};

/**
 * The clipboard, where there is no sharing sheet to hand the message to. It is refused outside a
 * secure context and in a few older engines, which the screen treats as "not copied" rather than
 * pretending otherwise.
 */
const toClipboard = async (message: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(message);
    return true;
  } catch {
    return false;
  }
};

/**
 * The kind of phone, for the account's list of devices: "Android", "iPhone", "Windows". Read from
 * the browser, never typed, and never more than the platform's name.
 */
const deviceLabel = (): string => {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const known = [
    ['iPhone', /iPhone/u],
    ['iPad', /iPad/u],
    ['Android', /Android/u],
    ['Windows', /Windows/u],
    ['Mac', /Macintosh/u],
    ['Linux', /Linux/u],
  ] as const;
  return known.find(([, pattern]) => pattern.test(agent))?.[0] ?? '';
};

const whenIdle = (): void => {
  const fetchIt = (): void => {
    void preloadPrintSanitizer();
    void preloadPrintTemplates();
    // The rest of the French, so the next screen does not wait for it.
    void loadFrenchDeferred().catch(() => undefined);
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fetchIt, { timeout: 4000 });
    return;
  }
  setTimeout(fetchIt, 2000);
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Every screen past the first is fetched while the phone is idle, so opening one rarely waits.
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withPreloading(PreloadAllModules),
      // A step back refused by a screen (« Quitter sans enregistrer ? » → Rester) is undone by
      // stepping forward again, so the history keeps its entries and the next step back lands
      // where it should; the default would overwrite the entry the browser had moved to.
      withRouterConfig({ canceledNavigationResolution: 'computed' }),
    ),
    { provide: PublicJourneyUseCases, useFactory: publicJourneyUseCases },
    { provide: PublicJourneyConfig, useFactory: publicJourneyConfig },
    // Creating the phrase draws on the same use cases and the same draft as the journey does.
    {
      provide: PassphraseCreationUseCases,
      useFactory: (): PassphraseCreationUseCases => {
        const journey = inject(PublicJourneyUseCases);
        return {
          beginPassphraseOnboarding: journey.beginPassphraseOnboarding,
          confirmPassphraseOnboarding: journey.confirmPassphraseOnboarding,
          generateRecoveryCard: journey.generateRecoveryCard,
          // Drawn on the phone, from the list of the language being read; kept nowhere.
          suggestPassphrase: new SuggestPassphrase(new LazyWordLists(), new CryptoRandomIndex()),
        };
      },
    },
    {
      provide: PassphraseCreationConfig,
      useFactory: (): PassphraseCreationConfig => inject(PublicJourneyConfig),
    },
    provideTransloco({
      config: {
        availableLangs: LANGUAGES.map((language) => language.code),
        defaultLang: SOURCE_LANGUAGE,
        fallbackLang: SOURCE_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: RegistryTranslocoLoader,
    }),
    { provide: TRANSLOCO_MISSING_HANDLER, useClass: FrenchFallbackMissingHandler },
    { provide: TRANSLOCO_TRANSPILER, useClass: LocalisedNumbersTranspiler },
    provideEnvironmentInitializer(() => {
      const document = inject(DOCUMENT);
      const destroyRef = inject(DestroyRef);
      const transloco = inject(TranslocoService);

      const initialLanguage = storedLanguage() ?? browserLanguage() ?? SOURCE_LANGUAGE;
      // The first part of the French, for the first screen; the rest follows before any other.
      transloco.setTranslation(FRENCH_CORE as Translation, SOURCE_LANGUAGE);
      transloco.setActiveLang(initialLanguage);
      // setActiveLang does not fetch anything: without this the remembered catalogue is never
      // loaded, and every string falls back to French while the app claims to be in Arabic.
      transloco.load(initialLanguage).pipe(takeUntilDestroyed(destroyRef)).subscribe();
      transloco.langChanges$.pipe(takeUntilDestroyed(destroyRef)).subscribe((language) => {
        document.documentElement.lang = language;
        document.documentElement.dir = directionOf(language);
        rememberLanguage(language);
      });
    }),
    provideEnvironmentInitializer(() => {
      inject(PwaUpdateService);
      // The boot script already wrote the attribute; the store takes ownership of it from here.
      inject(ThemeStore).apply();
      whenIdle();
      if (RUNS_NATIVE) {
        // A backup opened from another app goes to the restore screen, which asks its phrase.
        const router = inject(Router);
        PHONE!.onBackupReceived((file) => {
          RECEIVED_BACKUP!.set(file);
          void router.navigateByUrl(`/${JOURNEY_PATHS.restore}`);
        });
        // Android's back button is the back arrow; a root tab has none and goes home, as the
        // journey's own way back does; on the first screen it leaves the app.
        PHONE!.onBackButton(() => {
          if (router.url.split(/[?#]/u)[0] === '/') return false;
          void router.navigateByUrl('/');
          return true;
        });
        // The screens of the phrase are protected from captures and from the app switcher.
        router.events.subscribe((event) => {
          if (event instanceof NavigationEnd)
            PHONE!.phraseScreen(isPhraseScreen(event.urlAfterRedirects));
        });
      }
    }),
    // The apps carry their files with them: no service worker, and nothing to update in place.
    provideServiceWorker('ngsw-worker.js', {
      enabled: RUNS_NATIVE ? false : !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
